import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { supabase } from '../lib/supabase';

const HeroSection = () => {
  const { openCart, cartCount } = useCart();
  const [images, setImages] = useState({
    left: null,
    right: null
  });
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Fetch hero images from Supabase
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const { data, error } = await supabase
          .from('site_images')
          .select('image_url, name')
          .eq('section', 'hero-images');

        if (error) throw error;

        if (data && data.length > 0) {
          const imageMap = {
            left: '/Left.png',
            right: '/Right.png'
          };
          data.forEach(img => {
            if (img.name?.toLowerCase() === 'left') {
              imageMap.left = img.image_url;
            } else if (img.name?.toLowerCase() === 'right') {
              imageMap.right = img.image_url;
            }
          });
          
          setImages(imageMap);
        } else {
          // Use fallbacks if no data
          setImages({
            left: '/Left.png',
            right: '/Right.png'
          });
        }
      } catch (error) {
        console.error('Error fetching hero images:', error);
        // Use fallback images if fetch fails
        setImages({
          left: '/Left.png',
          right: '/Right.png'
        });
      } finally {
        setImagesLoaded(true);
      }
    };

    fetchHeroImages();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.cdnfonts.com/css/tasa-orbiter-display');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        html, body, #root {
          height: 100%;
        }
        
        body {
          background-color: #0D0D0D;
          font-family: 'TASA Orbiter Display', sans-serif;
        }
        .nav-link {
          transition: opacity 0.2s ease;
        }
        .nav-link:hover {
          opacity: 1 !important;
        }
        .shop-button {
          transition: all 0.3s ease;
        }
        .shop-button:hover {
          background-color: rgba(255, 255, 255, 0.25) !important;
          backdrop-filter: blur(12px) !important;
        }
        .hero-image-container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: left center;
          transition: transform 0.6s ease, opacity 0.3s ease;
        }
        .hero-image-container:hover .hero-image {
          transform: scale(1.03);
        }
        
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          width: 100%;
          height: 100vh;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .hero-grid.loaded {
          opacity: 1;
        }
        
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr;
            height: auto;
          }
          .hero-image-container {
            height: 50vh;
            width: 100vw;
          }
          .hero-image {
            object-position: center center;
          }
        }
      `}</style>
      <div style={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#0D0D0D',
        fontFamily: "'TASA Orbiter Display', sans-serif",
        color: '#F1F1F1',
      }}>
        
        {/* Navigation - 80px height, 10px side margins */}
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: '80px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 10px',
        }}>
          {/* Collection Link */}
          <Link 
            to="/collection" 
            className="nav-link"
            style={{
              color: '#F1F1F1',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '400',
              opacity: 0.9,
              letterSpacing: '0.01em',
            }}
          >
            Collection
          </Link>
          {/* Logo - Centered */}
          <Link to="/" style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}>
            <img 
              src="/logo.png" 
              alt="Logo" 
              style={{
                height: '50px',
                width: 'auto',
              }}
            />
          </Link>
          {/* Cart Button */}
          <button
            onClick={openCart}
            className="nav-link"
            style={{
              background: 'none',
              border: 'none',
              color: '#F1F1F1',
              fontSize: '14px',
              fontWeight: '400',
              opacity: 0.9,
              letterSpacing: '0.01em',
              cursor: 'pointer',
              fontFamily: "'TASA Orbiter Display', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Cart
            {cartCount > 0 && (
              <span style={{
                backgroundColor: '#F1F1F1',
                color: '#0D0D0D',
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '18px',
                textAlign: 'center',
              }}>
                {cartCount}
              </span>
            )}
          </button>
        </header>
        {/* Full Viewport Image Grid - No margins, no gaps */}
        <div className={`hero-grid ${imagesLoaded ? 'loaded' : ''}`}>
          
          {/* Left Image - Apparel */}
          <div className="hero-image-container">
            <img 
              src={images.left}
              alt="Shop Apparel"
              className="hero-image"
            />
            
            {/* Shop Apparel Button - Centered */}
            <Link
              to="/collection?filter=apparel"
              className="shop-button"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 20px',
                color: '#F1F1F1',
                fontSize: '13px',
                fontWeight: '400',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                cursor: 'pointer',
                letterSpacing: '0.01em',
                textDecoration: 'none',
              }}
            >
              Shop Apparel
            </Link>
          </div>
          {/* Right Image - Prints */}
          <div className="hero-image-container">
            <img 
              src={images.right}
              alt="Shop Prints"
              className="hero-image"
            />
            
            {/* Shop Prints Button - Centered */}
            <Link
              to="/collection?filter=prints"
              className="shop-button"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 20px',
                color: '#F1F1F1',
                fontSize: '13px',
                fontWeight: '400',
                fontFamily: "'TASA Orbiter Display', sans-serif",
                cursor: 'pointer',
                letterSpacing: '0.01em',
                textDecoration: 'none',
              }}
            >
              Shop Prints
            </Link>
          </div>
        </div>
        {/* Tagline Section */}
        <section style={{
          backgroundColor: '#0D0D0D',
          padding: '80px 20px',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: '400',
            color: '#F1F1F1',
            marginBottom: '8px',
            letterSpacing: '-0.01em',
            lineHeight: 1.4,
          }}>
            Artificial images.
          </h2>
          <p style={{
            fontSize: '32px',
            fontWeight: '400',
            color: '#8A8A8A',
            letterSpacing: '-0.01em',
            lineHeight: 1.4,
          }}>
            Physical form.
          </p>
        </section>
      </div>
    </>
  );
};

export default HeroSection;